import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { ArrowUpDown, GripVertical } from "lucide-react";
import { blocksOf, lineIds, moveBlock } from "../lib/reorder.ts";
import { ordinal, type Entry } from "../lib/session.ts";

/**
 * The session's movements put in another order: a machine taken, a
 * movement done later than the sheet has it. Dragged by the grip, so the
 * rest of the card still scrolls the page under the thumb; the arrow keys
 * move it on a desktop. A superset and the circuit move in one piece.
 */
export function ReorderList({
  entries,
  saving,
  onSave,
  onCancel,
}: {
  entries: Entry[];
  saving: boolean;
  onSave: (sessionExerciseIds: number[]) => void;
  onCancel: () => void;
}) {
  const [blocks, setBlocks] = useState(() => blocksOf(entries));
  const [drag, setDrag] = useState<{ id: number; offset: number } | null>(null);
  // Where the pointer would be had the card not moved: shifted by each swap,
  // so the card stays under the finger as the others close up.
  const origin = useRef(0);
  const cards = useRef(new Map<number, HTMLLIElement>());

  const indexOf = (id: number) => blocks.findIndex((block) => block[0]!.id === id);

  const start = (event: PointerEvent<HTMLButtonElement>, id: number) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    origin.current = event.clientY;
    setDrag({ id, offset: 0 });
  };

  const move = (event: PointerEvent<HTMLButtonElement>) => {
    if (drag === null) return;
    let offset = event.clientY - origin.current;
    const index = indexOf(drag.id);
    const card = cards.current.get(drag.id)?.getBoundingClientRect();
    const rect = (block: Entry[] | undefined) =>
      block ? cards.current.get(block[0]!.id)?.getBoundingClientRect() : undefined;
    const next = rect(blocks[index + 1]);
    const previous = rect(blocks[index - 1]);
    if (card) {
      // The card's place in the list, before the drag moved it.
      const top = card.top - drag.offset;
      const bottom = card.bottom - drag.offset;
      const middle = (top + bottom) / 2;
      if (next && middle + offset > (next.top + next.bottom) / 2) {
        const shift = next.bottom - bottom;
        setBlocks(moveBlock(blocks, index, index + 1));
        origin.current += shift;
        offset -= shift;
      } else if (previous && middle + offset < (previous.top + previous.bottom) / 2) {
        const shift = previous.top - top;
        setBlocks(moveBlock(blocks, index, index - 1));
        origin.current += shift;
        offset -= shift;
      }
    }
    setDrag({ id: drag.id, offset });
  };

  const stop = () => setDrag(null);

  const step = (event: KeyboardEvent<HTMLButtonElement>, id: number) => {
    const by = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
    if (by === 0) return;
    event.preventDefault();
    const index = indexOf(id);
    setBlocks(moveBlock(blocks, index, index + by));
  };

  const ids = lineIds(blocks);
  const changed = ids.some((id, index) => entries[index]?.id !== id);

  return (
    <div>
      <p className="label mb-2">Glisser pour changer l'ordre</p>
      <ol className="space-y-2">
        {blocks.map((block, number) => {
          const id = block[0]!.id;
          const dragged = drag?.id === id;
          const circuit = block[0]!.circuitGroup;
          return (
            <li
              key={id}
              ref={(node) => {
                if (node) cards.current.set(id, node);
                else cards.current.delete(id);
              }}
              style={dragged ? { transform: `translateY(${drag.offset}px)` } : undefined}
              className={[
                "card relative flex items-center gap-4 py-2.5 pr-1 pl-4 select-none",
                dragged ? "z-10 border-hue/70 bg-surface-high shadow-lg" : "",
              ].join(" ")}
            >
              <span className="outline-num w-11 shrink-0 text-[1.9rem] leading-none">{ordinal(number)}</span>
              <span className="min-w-0 flex-1">
                {circuit !== null ? (
                  <>
                    <span className="line-clamp-2 block text-[1.02rem] leading-snug font-semibold">{circuit}</span>
                    <span className="block truncate text-[0.84rem] text-ink-mute">
                      {block.map((entry) => entry.exercise.name).join(", ")}
                    </span>
                  </>
                ) : (
                  block.map((entry, offset) => (
                    <span key={entry.id} className="block">
                      {offset > 0 && (
                        <span className="label flex items-center gap-1.5 py-0.5">
                          <ArrowUpDown size={12} aria-hidden /> en superset
                        </span>
                      )}
                      <span className="line-clamp-2 block text-[1.02rem] leading-snug font-semibold">
                        {entry.exercise.name}
                      </span>
                    </span>
                  ))
                )}
              </span>
              <button
                type="button"
                aria-label={`Déplacer ${circuit ?? block.map((entry) => entry.exercise.name).join(" et ")}`}
                onPointerDown={(event) => start(event, id)}
                onPointerMove={move}
                onPointerUp={stop}
                onPointerCancel={stop}
                onKeyDown={(event) => step(event, id)}
                className="flex h-12 w-12 shrink-0 cursor-grab touch-none items-center justify-center text-ink-mute active:cursor-grabbing active:text-ink"
              >
                <GripVertical size={22} aria-hidden />
              </button>
            </li>
          );
        })}
      </ol>

      <button
        type="button"
        disabled={saving}
        onClick={() => (changed ? onSave(ids) : onCancel())}
        className="slab mt-3 w-full rounded-card px-5 py-3.5 text-[1.05rem] font-bold disabled:opacity-60"
      >
        {saving ? "Enregistrement…" : "Valider l'ordre"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="mt-1 block h-11 w-full text-center text-[0.85rem] text-ink-mute hover:text-ink-soft active:text-ink"
      >
        Annuler
      </button>
    </div>
  );
}
